import axios, { type AxiosError, type AxiosResponse } from 'axios'

type RetriableRequestConfig = {
  _retry?: boolean
}

let refreshPromise: Promise<boolean> | null = null

export const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      const requestConfig = error.config as (typeof error.config & RetriableRequestConfig)
      if (requestConfig?._retry) {
        return Promise.reject(error)
      }

      // Try to refresh token
      const { useAuthStore } = await import('@/stores/auth')
      const auth = useAuthStore()

      if (!refreshPromise) {
        refreshPromise = auth.refresh().finally(() => {
          refreshPromise = null
        })
      }

      const refreshed = await refreshPromise
      if (!refreshed) {
        auth.login()
      } else if (requestConfig) {
        // Retry the original request
        requestConfig._retry = true
        return api.request(requestConfig)
      }
    }
    return Promise.reject(error)
  }
)

// Type-safe API helpers
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

export interface ApiError {
  detail?: unknown
  title?: string
  message?: string
  type?: string
  instance?: string
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error)
}

function extractValidationError(details: unknown): string | null {
  if (!Array.isArray(details) || details.length === 0) return null

  const first = details[0] as Record<string, unknown>
  const message = typeof first?.msg === 'string' ? first.msg : null
  if (message) return message

  const detail = typeof first?.detail === 'string' ? first.detail : null
  return detail
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    const responseData = error.response?.data as unknown

    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData
    }

    const detail = typeof responseData === 'object' && responseData !== null
      ? (responseData as ApiError).detail
      : undefined
    if (typeof detail === 'string' && detail.trim()) {
      return detail
    }

    const validationError = extractValidationError(detail)
    if (validationError) {
      return validationError
    }

    const message = typeof responseData === 'object' && responseData !== null
      ? (responseData as ApiError).message
      : undefined
    if (typeof message === 'string' && message.trim()) {
      return message
    }

    const title = typeof responseData === 'object' && responseData !== null
      ? (responseData as ApiError).title
      : undefined
    if (typeof title === 'string' && title.trim()) {
      return title
    }

    if (error.request && !error.response) {
      return 'Сервер недоступен. Проверьте соединение и попробуйте снова.'
    }
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'Произошла неизвестная ошибка'
}
